import { LayoutOutlined } from "@ant-design/icons";
import { Button, Form, Input, Tabs } from "antd";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { isScalarAttrType } from "src/business/attribute/isScalarAttrType";
import { deleteCatalog } from "src/business/catalog/apiCatalog";
import { ZCatalog } from "src/business/catalog/ZCatalog";
import { ExtensionBindingObjectType } from "src/business/extensionBindings";
import { EditRoleAccessList } from "src/business/permissions/EditRoleAccessList";
import { SpecificationAttrSelect } from "src/business/specification/SpecificationAttrSelect";
import { SpecificationSelect } from "src/business/specification/SpecificationSelect";
import { classNames } from "src/common/classNames";
import { mdmDeepEqual } from "src/common/mdmDeepEqual";
import { onError } from "src/common/onError";
import { maxNameLength } from "src/common/rxName";
import { trimRequired } from "src/common/validationRules";
import { FormBlocker } from "src/components/FormBlocker";
import { IdBox } from "src/components/IdBox";
import { msgQueueStore } from "src/components/MsgQueue";
import { BookIcon } from "src/icons";
import { ObjectCardHeader } from "src/pages/management/objects/common/ObjectCardHeader";
import * as layoutStyles from "src/pages/management/objects/common/ObjectCardLayout.module.less";
import { ObjectCardTabPane } from "src/pages/management/objects/common/ObjectCardTabPane";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

import { SysDataFormItems } from "../../../../../business/sysData/SysDataFormItems";
import { EventsAndExtensionsTab } from "../../common/EventsAndExtensions";
import { CatalogCardActions } from "./CatalogCardActions";
import { CatalogCardStore } from "./CatalogCardStore";
import { catalog2edit, CatalogCardValues } from "./CatalogCardValues";
import { ClassifiersSelect } from "./ClassifiersSelect";
import * as styles from "./ManagementCatalogCard.module.less";

type PropsManagementCatalogCard = {
  title: string;
  okText: string;
  srcData?: ZCatalog;
  cardStore: CatalogCardStore;
  submit(values: CatalogCardValues): Promise<ZCatalog>;
  onSuccess?(result: ZCatalog): void;
};

const field = (name: keyof CatalogCardValues): string => name;

enum CatalogTabKey {
  General = "1",
  Classifiers = "2",
  Events = "events",
  SystemData = "systemData",
}

export const ManagementCatalogCard: React.FC<PropsManagementCatalogCard> =
  observer((props) => {
    const navigate = useNavigate();
    const { title, okText, srcData, cardStore, submit, onSuccess } = props;
    const { tab } = cardStore;
    const [form] = Form.useForm();
    const isNew = !srcData?.id;
    const initialValues = catalog2edit(srcData);
    const [isChanged, setIsChanged] = React.useState(false);

    const schemaValue = Form.useWatch(field("schema"), form);

    React.useEffect(() => {
      cardStore.setExpired(false);
      form.resetFields();
      form.setFieldsValue(initialValues);
      setIsChanged(false);
    }, [srcData]);

    const deleteTask = async () => {
      if (srcData?.id) {
        await deleteCatalog(srcData.id);
        cardStore.setExpired(true);
        navigate(makeUrl(PageUrl.managementCatalogs));
      }
    };

    const tabsItems = React.useMemo(() => {
      const items = [
        { key: CatalogTabKey.General, label: "Общее" },
        { key: CatalogTabKey.Classifiers, label: "Классификаторы" },
      ];
      if (!isNew) {
        items.push({
          key: CatalogTabKey.Events,
          label: "События и расширения",
        });
        items.push({
          key: CatalogTabKey.SystemData,
          label: "Системные данные",
        });
      }
      return items;
    }, [isNew]);

    return (
      <Form<CatalogCardValues>
        form={form}
        layout="vertical"
        className={layoutStyles.formRoot}
        onFinish={(values) => {
          cardStore
            .onSubmit(values, submit)
            .then((res) => {
              // Внутри onSuccess возможен редирект
              cardStore.setExpired(true);
              // (catalog2edit(res));
              onSuccess?.(res);
            })
            .catch(onError);
        }}
        onFinishFailed={() => {
          // Так как на форме несколько вкладок, ошибочные поля могут оказаться не видны.
          // Это дешёвый способ сообщить, что есть проблемы :)
          msgQueueStore.add({
            type: "error",
            message: "Не все поля заполнены правильно",
          });
        }}
        onValuesChange={(_, all) => {
          setIsChanged(!mdmDeepEqual(initialValues, all));
        }}
      >
        <FormBlocker
          initialData={initialValues}
          form={form}
          expired={cardStore.expired}
        />
        <ObjectCardHeader
          title={title}
          submitText={okText}
          submitLoading={cardStore.saving}
          submitHtmlType="submit"
        >
          {isChanged && "*"}
          {!isNew && (
            <>
              <Link to={makeUrl(PageUrl.records, { id: srcData.id })}>
                <Button
                  type="text"
                  icon={<BookIcon />}
                  className={styles.backButton}
                >
                  К списку записей
                </Button>
              </Link>
              <CatalogCardActions deleteTask={deleteTask} />
              <Link
                to={makeUrl(PageUrl.managementCatalogAccess, {
                  id: srcData.id,
                })}
              >
                <Button type="default" icon={<LayoutOutlined />}>
                  Настроить представление
                </Button>
              </Link>
            </>
          )}
        </ObjectCardHeader>

        <div className={layoutStyles.tabsContainer}>
          <Tabs
            items={tabsItems}
            activeKey={cardStore.tab}
            onChange={(activeKey) => cardStore.setTab(activeKey)}
          />
          <div className={layoutStyles.tabContent}>
            <ObjectCardTabPane
              className={layoutStyles.commonBlock}
              hidden={tab !== CatalogTabKey.General}
              hiddenClassName={layoutStyles.hidden}
            >
              <Form.Item
                name={field("name")}
                label="Название"
                rules={[trimRequired(), { max: maxNameLength }]}
              >
                <Input allowClear disabled={!isNew} maxLength={maxNameLength} />
              </Form.Item>

              <IdBox entity={srcData} />

              <Form.Item name={field("description")} label="Описание">
                <Input.TextArea allowClear />
              </Form.Item>

              <Form.Item
                name={field("schema")}
                label="Первичная спецификация"
                rules={[{ required: true, message: t("Field required") }]}
              >
                <SpecificationSelect
                  specType="primary"
                  // Есть требование: Запретить изменение первичной спецификации если она уже выбрана по объекту.
                  disabled={!isNew}
                  onChange={() => {
                    form.setFieldValue(field("displayAttribute"), null);
                  }}
                />
              </Form.Item>

              <Form.Item
                name={field("displayAttribute")}
                label="Атрибут для отображения"
                rules={[{ required: true, message: t("Field required") }]}
              >
                <SpecificationAttrSelect
                  specId={schemaValue?.id}
                  allowClear
                  filterFn={isScalarAttrType}
                />
              </Form.Item>

              {/* TODO: Не очень понятна логика, почему классификаторы и системные данные вынесены в отдельные вкладки,
              а настройка прав - нет. Но в случае необходимости это сделать не сложно */}

              <Form.Item name={field("permissions")}>
                <EditRoleAccessList />
              </Form.Item>
            </ObjectCardTabPane>

            <div
              className={classNames([
                styles.clsBlock,
                layoutStyles.tabPaneInset,
                [tab !== CatalogTabKey.Classifiers, layoutStyles.hidden],
              ])}
            >
              <Form.Item name={field("classifiers")} className={styles.clsItem}>
                <ClassifiersSelect
                  tableStore={cardStore.clsStore}
                  loader={cardStore.clsLoader}
                />
              </Form.Item>
            </div>

            <ObjectCardTabPane
              className={layoutStyles.tabPaneInset}
              hidden={isNew || tab !== CatalogTabKey.Events}
              hiddenClassName={layoutStyles.hidden}
            >
              <Form.Item name={field("extensions")}>
                <EventsAndExtensionsTab
                  scope={ExtensionBindingObjectType.Catalog}
                />
              </Form.Item>
            </ObjectCardTabPane>

            <ObjectCardTabPane
              className={layoutStyles.commonBlock}
              hidden={isNew || tab !== CatalogTabKey.SystemData}
              hiddenClassName={layoutStyles.hidden}
            >
              <SysDataFormItems data={srcData} />
            </ObjectCardTabPane>
          </div>
        </div>
      </Form>
    );
  });
